import { useMemo } from "react";
import {
  createFileRoute,
  useNavigate,
  type NavigateOptions,
} from "@tanstack/react-router";

// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import NotPermission from "@/components/NotPermission";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { TableLoading } from "@/components/Skeleton";
import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import DinamixSkeletonTable from "@/components/skeleton/DinamixTableSkeleton";
import Pagination from "@/components/Pagination";

// Icons
import { CirclePlus, Eye } from "lucide-react";

// Services
import { formatNumber } from "@/utils/formatNumber";
import { ErrorResponse } from "@/types/ErrorResponse";
import type { AxiosError } from "axios";

interface ActionPlanItem {
  id: string;
  yearly: number;
  planName: string;
  totalIncome: number;
  totalExpense: number;
  totalProfit: number;
  updatedAt?: string;
}

interface ActionPlanResponse {
  status: boolean;
  message: string;
  total: number;
  data: ActionPlanItem[];
}

interface EditProps {
  year: number;
  id: string;
}
import type { FC } from "react";
import { getActionPlan } from "@/services/action-plan";
import { useQuery } from "@tanstack/react-query";
import { clearLocalStorage } from "@/helpers/accessLocalStorage";
export const Route = createFileRoute("/_layout-private/report/action-plan/")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      page: Number(search.page) || 1,
      startYear: Number(search.startYear) || 0,
      endYear: Number(search.endYear) || 0,
      // search: (search.search as string) || "",
      // lawTypeId: (search.lawTypeId as string) || "",
      // updatedAt: (search.updatedAt as string) || "",
    };
  },
});

function RouteComponent() {
  const {
    page,
    startYear: startYearParam,
    endYear: endYearParam,
  } = Route.useSearch();
  const navigate = useNavigate();

  const initialZero = 0;
  const currentYear = new Date().getFullYear();

  const [filter, setFilter] = useState({
    skip: (page - 1) * 40,
    limit: 40,
    startYear: startYearParam || initialZero,
    endYear: endYearParam || initialZero,
  });
  const id = localStorage.getItem("companyId");

  const { operationPlan } = useAccessPermission();

  // Query
  const {
    data: actionPlan,
    isLoading,
    error,
  } = useQuery<ActionPlanResponse, AxiosError<ErrorResponse>>({
    queryKey: ["actionPlan", id, filter.startYear, filter.endYear, page],
    queryFn: () =>
      getActionPlan({
        skip: (page - 1) * 40,
        limit: 40,
        companyId: id ? id.replace(/^"(.+)"$/, "$1") : "",
        startYear:
          filter.startYear === initialZero ? undefined : filter.startYear,
        endYear: filter.endYear === initialZero ? undefined : filter.endYear,
      }),
    enabled: !!id,
  });

  if (error) {
    if (error?.response?.data?.message === "LOGGED_OUT") {
      console.log("clearing storage due to logged out");
      clearLocalStorage();
      window.location.reload();
    }
  }

  const yearOptions = useMemo(() => {
    return [...Array(currentYear - 1975 + 1)].map((_, index) =>
      (currentYear - index).toString()
    );
  }, [currentYear]);

  const endYearOptions = useMemo(() => {
    if (!filter.startYear || filter.startYear === initialZero) {
      return yearOptions;
    }
    const startYearIndex = yearOptions.findIndex(
      (year) => Number.parseInt(year) === filter.startYear
    );
    if (startYearIndex === -1) return yearOptions;
    return yearOptions.slice(0, startYearIndex + 1);
  }, [filter.startYear, yearOptions]);

  const handleStartYearChange = (value: string) => {
    const yearValue = Number(value);
    setFilter((prev) => ({ ...prev, startYear: yearValue }));

    if (filter.endYear !== initialZero && filter.endYear < yearValue) {
      setFilter((prev) => ({ ...prev, endYear: yearValue }));
      navigate({
        search: (prev) => ({
          ...prev,
          startYear: yearValue,
          endYear: yearValue,
          page: 1,
        }),
      } as NavigateOptions);
    } else {
      navigate({
        search: (prev) => ({
          ...prev,
          startYear: yearValue,
          page: 1,
        }),
      } as NavigateOptions);
    }
  };

  const handleEndYearChange = (value: string) => {
    const yearValue = Number(value);
    setFilter((prev) => ({ ...prev, endYear: yearValue }));
    navigate({
      search: (prev) => ({
        ...prev,
        endYear: yearValue,
        page: 1,
      }),
    } as NavigateOptions);
  };

  const handlePageClick = (page: number) => {
    navigate({
      search: (prev) => ({
        ...prev,
        page,
      }),
    } as NavigateOptions);
  };

  // console.log("data,", actionPlan.data);

  const Edit: FC<EditProps> = ({ year, id }) => {
    const navigate = useNavigate();

    return (
      <div className="flex gap-x-2 items-center">
        <Button
          onClick={() => {
            localStorage.setItem("id", JSON.stringify(id));
            localStorage.setItem("year", JSON.stringify(year));
            navigate({ to: "/report/action-plan/edit" });
          }}
          variant="secondary"
          className="flex items-center text-blue-500 rounded-md border border-gray-300"
        >
          ແກ້ໄຂ
        </Button>
        ແກ້ໄຂພາຍໃນ 3 ວັນ
      </div>
    );
  };

  return operationPlan.isRead ? (
    <section>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbPage>ລາຍງານແຜນດຳເນີນງານ</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>ລາຍງານແຜນດຳເນີນງານ</CardTitle>
          <CardDescription>ລາຍການລາຍງານແຜນດຳເນີນງານ</CardDescription>
        </CardHeader>
        {isLoading ? (
          <DinamixSkeletonTable />
        ) : (
          <CardContent>
            <div className="flex justify-between items-end mt-5 mb-5 w-full">
              <div className="flex gap-x-2 items-center">
                <div>
                  <Label htmlFor="">ປີເລີ່ມຕົ້ນ</Label>
                  <Select
                    value={filter.startYear.toString()}
                    onValueChange={handleStartYearChange}
                  >
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="ເລືອກປີ" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">ທັງໝົດ</SelectItem>
                      {yearOptions.map((year) => (
                        <SelectItem key={year} value={year}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="">ປີສິ້ນສຸດ</Label>
                  <Select
                    value={filter.endYear.toString()}
                    onValueChange={handleEndYearChange}
                    disabled={
                      !filter.startYear || filter.startYear === initialZero
                    }
                  >
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="ເລືອກປີ" />
                    </SelectTrigger>
                    <SelectContent>
                      {endYearOptions.map((year) => (
                        <SelectItem key={year} value={year}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                {operationPlan.isWrite && (
                  <Button
                    className="flex gap-2 items-center"
                    onClick={() =>
                      navigate({ to: "/report/action-plan/addnew" })
                    }
                  >
                    <CirclePlus size={16} /> ເພີ່ມແຜນດຳເນີນງານ
                  </Button>
                )}
              </div>
            </div>
            <div className="overflow-x-auto w-full rounded-sm border">
              <div>
                <Table className="mb-4 border-none">
                  <TableHeader>
                    <TableRow>
                      <TableHead>ລໍາດັບ</TableHead>
                      <TableHead>ຊື່ເອກະສານ</TableHead>
                      <TableHead>ແຜນລາຍຮັບ</TableHead>
                      <TableHead>ແຜນລາຍຈ່າຍ</TableHead>
                      <TableHead>ແຜນກຳໄລ/(ຂາດທຶນ)ສຸດທິ</TableHead>
                      <TableHead>ປະຈຳປີ</TableHead>
                      {operationPlan?.isUpdate && <TableHead>ຈັດການ</TableHead>}
                    </TableRow>
                  </TableHeader>
                  {isLoading ? (
                    <TableLoading />
                  ) : (
                    <TableBody>
                      {actionPlan?.data?.map(
                        (invoice: ActionPlanItem, index: number) => (
                          <TableRow key={invoice.id} className="cursor-pointer">
                            {/* Index/ID Cell */}
                            <TableCell
                              className="font-medium text-left"
                              onClick={() => {
                                localStorage.setItem(
                                  "id",
                                  JSON.stringify(invoice.id)
                                );
                                localStorage.setItem(
                                  "year",
                                  JSON.stringify(invoice.yearly)
                                );
                                navigate({
                                  to: `/report/action-plan/${invoice.id}yearly=${invoice.yearly}`,
                                });
                              }}
                            >
                              {index + 1}
                            </TableCell>

                            {/* Plan Name Cell */}
                            <TableCell
                              className=""
                              onClick={() => {
                                localStorage.setItem(
                                  "id",
                                  JSON.stringify(invoice.id)
                                );
                                localStorage.setItem(
                                  "year",
                                  JSON.stringify(invoice.yearly)
                                );
                                navigate({
                                  to: `/report/action-plan/${invoice.id}yearly=${invoice.yearly}`,
                                });
                              }}
                            >
                              <div className="flex gap-x-2 items-center font-medium">
                                <Eye className="text-[#007AFF]" />
                                {invoice.planName}
                              </div>
                            </TableCell>

                            {/* Total Income Cell */}
                            <TableCell
                              className="font-medium text-right"
                              onClick={() => {
                                localStorage.setItem(
                                  "id",
                                  JSON.stringify(invoice.id)
                                );
                                localStorage.setItem(
                                  "year",
                                  JSON.stringify(invoice.yearly)
                                );
                                navigate({
                                  to: `/report/action-plan/${invoice.id}yearly=${invoice.yearly}`,
                                });
                              }}
                            >
                              {formatNumber(invoice.totalIncome)}
                            </TableCell>

                            {/* Total Expense Cell */}
                            <TableCell
                              className="font-medium text-right"
                              onClick={() => {
                                localStorage.setItem(
                                  "id",
                                  JSON.stringify(invoice.id)
                                );
                                localStorage.setItem(
                                  "year",
                                  JSON.stringify(invoice.yearly)
                                );
                                navigate({
                                  to: `/report/action-plan/${invoice.id}yearly=${invoice.yearly}`,
                                });
                              }}
                            >
                              {formatNumber(invoice.totalExpense)}
                            </TableCell>

                            {/* Total Profit Cell */}
                            <TableCell
                              className="font-medium text-right"
                              onClick={() => {
                                localStorage.setItem(
                                  "id",
                                  JSON.stringify(invoice.id)
                                );
                                localStorage.setItem(
                                  "year",
                                  JSON.stringify(invoice.yearly)
                                );
                                navigate({
                                  to: `/report/action-plan/${invoice.id}yearly=${invoice.yearly}`,
                                });
                              }}
                            >
                              {formatNumber(invoice.totalProfit)}
                            </TableCell>

                            {/* Yearly Cell */}
                            <TableCell
                              className="font-medium text-left"
                              onClick={() => {
                                localStorage.setItem(
                                  "id",
                                  JSON.stringify(invoice.id)
                                );
                                localStorage.setItem(
                                  "year",
                                  JSON.stringify(invoice.yearly)
                                );
                                navigate({
                                  to: `/report/action-plan/${invoice.id}yearly=${invoice.yearly}`,
                                });
                              }}
                            >
                              {invoice.yearly}
                            </TableCell>

                            {/* Edit Cell */}
                            {operationPlan.isUpdate && (
                              <TableCell>
                                <Edit year={invoice.yearly} id={invoice.id} />
                              </TableCell>
                            )}
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  )}
                </Table>
              </div>
            </div>
            <Pagination
              handlePageClick={handlePageClick}
              totalItems={actionPlan?.total || 0}
              itemsPerPage={40}
              currentPage={page}
            />
          </CardContent>
        )}
      </Card>
    </section>
  ) : (
    <NotPermission />
  );
}
